import { useEffect, useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { GraduationCap, Play, CheckCircle2, Lock, Award } from 'lucide-react';
import { toast } from 'sonner';
import TrainingPlayer from './TrainingPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TrainingLauncher
 * Shows the current user's available training modules + per-module status.
 * Used inside Staff/PM/Tenant dashboards.
 */
export default function TrainingLauncher({ dataTestId = 'training-launcher' }) {
  const [modules, setModules] = useState([]);
  const [completions, setCompletions] = useState({});
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(false);
  const [eligible, setEligible] = useState(false);

  const fetchModules = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/modules`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setModules(data.modules || []);
        setCompletions(data.completions || {});
      }
    } catch (_e) { /* noop */ }
  }, []);

  const fetchEligibility = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/certificate/eligibility`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEligible(!!data.eligible);
      }
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { fetchModules(); fetchEligibility(); }, [fetchModules, fetchEligibility]);

  const downloadCertificate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/certificate`, { credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Could not generate certificate');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NECS_Certificate.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (_e) {
      toast.error('Download failed');
    }
  };

  // Auto-open the first required pending module if gating enabled
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/training/required-pending`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const first = (data.required || [])[0];
        if (first && data.gate) {
          setActive(first);
          setForced(true);
          setOpen(true);
        }
      } catch (_e) { /* noop */ }
    })();
  }, []);

  if (modules.length === 0) return null;

  return (
    <Card className="bg-zinc-900 border-l-4 border-l-[#C9A962] p-5" data-testid={dataTestId}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-[#C9A962]" />
          <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            TRAINING
          </h3>
        </div>
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">
          {Object.values(completions).filter((c) => c.completed_at).length} / {modules.length} completed
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {modules.map((m) => {
          const comp = completions[m.module_id];
          const done = !!comp?.completed_at;
          return (
            <div
              key={m.module_id}
              className="rounded-lg p-4 border border-white/10 bg-[#222] flex flex-col gap-2"
              data-testid={`training-card-${m.module_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white font-semibold leading-tight">{m.title}</p>
                  {m.description && <p className="text-xs text-zinc-400 mt-1">{m.description}</p>}
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {m.required && (
                    <Badge className="bg-[#C9A962] text-[#2D2D2D] text-[10px] tracking-wide">REQUIRED</Badge>
                  )}
                  {done && (
                    <Badge className="bg-green-600 text-white text-[10px] tracking-wide">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> DONE
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                className="font-bold mt-1 self-start"
                style={{ backgroundColor: done ? 'transparent' : '#C9A962', color: done ? '#C9A962' : '#2D2D2D', border: done ? '1px solid #C9A962' : 'none' }}
                onClick={() => { setActive(m); setForced(false); setOpen(true); }}
                data-testid={`training-launch-${m.module_id}`}
              >
                <Play className="h-3.5 w-3.5 mr-1" />
                {done ? 'Replay' : (comp?.started_at ? 'Continue' : 'Start')}
              </Button>
            </div>
          );
        })}
      </div>

      <TrainingPlayer
        module={active}
        open={open}
        onOpenChange={setOpen}
        forced={forced}
        onCompleted={() => { fetchModules(); fetchEligibility(); setForced(false); }}
      />

      {eligible && (
        <div
          className="mt-3 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
          style={{
            background: 'radial-gradient(circle at 0% 50%, rgba(201,169,98,0.18), transparent 60%), #1a1a1a',
            border: '1px solid #C9A962',
          }}
          data-testid="training-certificate-row"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Award className="h-6 w-6 text-[#C9A962] shrink-0" />
            <div className="min-w-0">
              <p className="text-white font-bold leading-tight">All Required Training Completed</p>
              <p className="text-xs text-zinc-400">Download your branded Certificate of Completion.</p>
            </div>
          </div>
          <Button
            className="font-bold"
            style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
            onClick={downloadCertificate}
            data-testid="training-certificate-download"
          >
            <Award className="h-4 w-4 mr-1" /> Get Certificate
          </Button>
        </div>
      )}

      {forced && (
        <p className="text-[11px] text-amber-400 mt-3 flex items-center gap-1">
          <Lock className="h-3 w-3" /> A required training is in progress — please finish to continue.
        </p>
      )}
    </Card>
  );
}
